import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useApi from "../../../shared/hooks/useApi";
import { useCallbackRef } from "../../../shared/hooks/useCallbackRef";
import { PatientId } from "../../../shared/schema/schema";
import { Medication } from "../types/medication.types";
import AddEditMedicationModal from "./AddEditMedicationModal";

interface Props {
  /** Shows the medications without add / edit / delete / "not taking" changes. */
  isReadOnly?: boolean;
  patientId: PatientId;
  /** After each successful change (add, edit, delete, "not taking any medications"). */
  onChanged?: () => void;
  /** Whether an add / edit / delete dialog is open (closing the page would lose it). */
  onFormOpenChange?: (isOpen: boolean) => void;
  /** Whether the "not taking any medications" change is saving (it has no dialog). */
  onSavingChange?: (isSaving: boolean) => void;
}

export default function MedicationTable({
  isReadOnly = false,
  patientId,
  onChanged,
  onFormOpenChange,
  onSavingChange,
}: Props) {
  const queryClient = useQueryClient();
  const { queries, mutations } = useApi();
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [deletingMedication, setDeletingMedication] = useState<Medication | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const medications = useQuery(queries.patient.medications(patientId));

  const deleteMutation = useMutation({
    ...mutations.patient.removeMedication(patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.patient.medications(patientId).queryKey });
      handleDeleteModalClose();
      onChanged?.();
    },
  });

  const notTakingMedicationsMutation = useMutation({
    ...mutations.patient.updateNotTakingMedications(patientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.patient.medications(patientId).queryKey });
      onChanged?.();
    },
  });

  const reportFormOpen = useCallbackRef(onFormOpenChange);
  const isFormOpen = isAddModalOpen || editingMedication !== null || isDeleteModalOpen;
  useEffect(() => {
    reportFormOpen(isFormOpen);
  }, [isFormOpen, reportFormOpen]);

  const reportSaving = useCallbackRef(onSavingChange);
  const isSavingNotTaking = notTakingMedicationsMutation.isPending;
  useEffect(() => {
    reportSaving(isSavingNotTaking);
  }, [isSavingNotTaking, reportSaving]);

  const handleEdit = (medication: Medication) => {
    setEditingMedication(medication);
  };

  const handleDelete = (medication: Medication) => {
    setDeletingMedication(medication);
    onDeleteModalOpen();
  };

  const handleEditModalClose = () => {
    setEditingMedication(null);
  };

  const handleDeleteModalClose = () => {
    setDeletingMedication(null);
    onDeleteModalClose();
  };

  const confirmDelete = () => {
    if (deletingMedication) {
      deleteMutation.mutate(deletingMedication.id);
    }
  };

  switch (medications.status) {
    case "error":
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading medications</AlertTitle>
          <AlertDescription>Please try again later.</AlertDescription>
        </Alert>
      );

    case "pending":
      return <Text>Loading medications...</Text>;

    case "success":
      return (
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="semibold">
              Medications
            </Text>
            {!isReadOnly && !medications.data.notTakingMedications && (
              <Button colorScheme="blue" onClick={() => setIsAddModalOpen(true)}>
                Add Medication
              </Button>
            )}
          </HStack>

          <Checkbox
            isChecked={medications.data.notTakingMedications}
            isDisabled={isReadOnly || notTakingMedicationsMutation.isPending}
            onChange={(e) =>
              notTakingMedicationsMutation.mutate({ notTakingMedications: e.target.checked })
            }
          >
            Patient is not taking any medications
          </Checkbox>

          {medications.data.notTakingMedications ? (
            <Box color="gray.500" py={8} textAlign="center">
              <Text fontSize="lg">Patient is not taking any medications.</Text>
            </Box>
          ) : medications.data.medications.length === 0 ? (
            <Box color="gray.500" py={8} textAlign="center">
              <Text fontSize="lg">No medications added yet.</Text>
              {!isReadOnly && (
                <Text fontSize="md">Click &ldquo;Add Medication&ldquo; to get started.</Text>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="md">
                <Thead>
                  <Tr>
                    <Th>Medication Name</Th>
                    <Th>Dose</Th>
                    <Th>Frequency</Th>
                    <Th>Administration Notes</Th>
                    {!isReadOnly && <Th width="120px">Actions</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {medications.data.medications.map((medication) => (
                    <Tr key={medication.id}>
                      <Td>
                        <Text fontWeight="medium">{medication.name}</Text>
                      </Td>
                      <Td>{medication.dosage}</Td>
                      <Td>{medication.frequency}</Td>
                      <Td>{medication.administrationNotes || "-"}</Td>
                      {!isReadOnly && (
                        <Td>
                          <HStack spacing={1}>
                            <IconButton
                              aria-label="Edit medication"
                              icon={<EditIcon />}
                              size="md"
                              variant="ghost"
                              onClick={() => handleEdit(medication)}
                            />
                            <IconButton
                              aria-label="Delete medication"
                              colorScheme="red"
                              icon={<DeleteIcon />}
                              size="md"
                              variant="ghost"
                              onClick={() => handleDelete(medication)}
                            />
                          </HStack>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <AddEditMedicationModal
            isOpen={isAddModalOpen}
            patientId={patientId}
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={onChanged}
          />

          <AddEditMedicationModal
            key={editingMedication?.id ?? "add"}
            isOpen={editingMedication !== null}
            medication={editingMedication}
            patientId={patientId}
            onClose={handleEditModalClose}
            onSuccess={onChanged}
          />

          <Modal isOpen={isDeleteModalOpen} onClose={handleDeleteModalClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Delete Medication</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>
                  Are you sure you want to delete{" "}
                  <Text as="span" fontWeight="bold">
                    {deletingMedication?.name}
                  </Text>
                  ? This action cannot be undone.
                </Text>
              </ModalBody>
              <ModalFooter>
                <Button mr={3} variant="ghost" onClick={handleDeleteModalClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={deleteMutation.isPending}
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </VStack>
      );
  }
}
